import { Dayjs } from "dayjs";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { LineDot } from "../../../../../../assets/icons/LineDot";
import { StyledLegend } from "../../../../../../components/CustomChartLegend/CustomChartLegend.styles";
import { getFriendlySourceName } from "../../../../../../utils/chartSourceUtils";
import { formatMeasurementAverageRangeDates } from "../../../../../../utils/chartUtils";
import { formatHoursAndMinutes } from "../../../../../../utils/dateFormatter";
import {
  ChartLegendContainer,
  Container,
  DateLabel,
  Divider,
  MainValueContainer,
  MainValuesContainer,
  NumberLabel,
  TextLabel,
} from "./CustomFitbitModalChartLegend.styles";
import { FitbitChartRow } from "./FitbitActivityChart";

type CustomFitbitModalChartLegendProps = {
  data: FitbitChartRow[];
  startDate: Dayjs;
  endDate: Dayjs;
  sources?: string[];
};

export function CustomFitbitModalChartLegend({
  data,
  startDate,
  endDate,
  sources,
}: CustomFitbitModalChartLegendProps) {
  const { t } = useTranslation();

  const { disabled, meanActive, meanSedentary, dateRange } = useMemo(() => {
    const hasData =
      Array.isArray(data) &&
      data.length > 0 &&
      data.some((d) => (d.activeHours ?? 0) > 0 || (d.sedentaryHours ?? 0) > 0);

    if (!hasData) {
      return {
        disabled: true,
        meanActive: null,
        meanSedentary: null,
        dateRange: null,
      };
    }

    const totalActive = data.reduce((acc, d) => acc + (d.activeHours || 0), 0);
    const totalSedentary = data.reduce(
      (acc, d) => acc + (d.sedentaryHours || 0),
      0
    );
    const count = data.length;

    const avgActive = count > 0 ? totalActive / count : 0;
    const avgSedentary = count > 0 ? totalSedentary / count : 0;

    return {
      disabled: false,
      meanActive: formatHoursAndMinutes(avgActive),
      meanSedentary: formatHoursAndMinutes(avgSedentary),
      dateRange: formatMeasurementAverageRangeDates(startDate, endDate),
    };
  }, [data, startDate, endDate]);

  const sourceSuffix =
    sources && sources.length > 0
      ? `, ${sources
          .map((source) => getFriendlySourceName(source, t))
          .join(" / ")}`
      : "";

  const chartLegendContainer = (
    <ChartLegendContainer disabled={disabled}>
      <StyledLegend key="Active">
        <LineDot fill="#C87C36" />
        {`${t(
          "patientPage_measurementModal_activity_legendActive"
        )}${sourceSuffix}`}
      </StyledLegend>
      <StyledLegend key="Sedentary">
        <LineDot fill="#54A6AA" />
        {`${t(
          "patientPage_measurementModal_activity_legendSedentary"
        )}${sourceSuffix}`}
      </StyledLegend>
    </ChartLegendContainer>
  );

  const mainValuesContainer = (
    <MainValuesContainer>
      <MainValueContainer>
        <NumberLabel disabled={disabled}>{meanActive ?? "-"}</NumberLabel>
        <TextLabel disabled={disabled}>
          {t("patientPage_measurementModal_legend_meanActive")}
        </TextLabel>
        <DateLabel disabled={disabled}>{dateRange ?? "-"}</DateLabel>
      </MainValueContainer>
      <MainValueContainer>
        <NumberLabel disabled={disabled}>{meanSedentary ?? "-"}</NumberLabel>
        <TextLabel disabled={disabled}>
          {t("patientPage_measurementModal_legend_meanSedentary")}
        </TextLabel>
        <DateLabel disabled={disabled}>{dateRange ?? "-"}</DateLabel>
      </MainValueContainer>
    </MainValuesContainer>
  );

  return (
    <div style={{ bottom: -16, position: "relative" }}>
      <Container>
        {mainValuesContainer}
        <Divider />
        {chartLegendContainer}
      </Container>
    </div>
  );
}
